import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
interface MonteCarloVisualization3DProps {
  chartData: Array<{
    day: number;
    median: number;
    p5: number;
    p95: number;
  }>;
  timeHorizon: number;
  currentValue: number;
}
const MonteCarloVisualization3D: React.FC<MonteCarloVisualization3DProps> = ({
  chartData,
  timeHorizon,
  currentValue
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  useEffect(() => {
    if (!containerRef.current || !chartData || chartData.length === 0) return;
    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;
    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0f172a);
    sceneRef.current = scene;
    // Camera setup
    const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000);
    camera.position.set(8, 4, 8);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;
    // Renderer setup
    const renderer = new THREE.WebGLRenderer({
      antialias: true
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;
    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);
    const directionalLight1 = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight1.position.set(10, 10, 5);
    scene.add(directionalLight1);
    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.3);
    directionalLight2.position.set(-10, -10, -5);
    scene.add(directionalLight2);
    // Grid helper
    const gridHelper = new THREE.GridHelper(12, 20, 0x444444, 0x222222);
    gridHelper.position.y = -4.5;
    scene.add(gridHelper);
    // Normalize data
    const maxValue = Math.max(...chartData.map(d => d.p95));
    const minValue = Math.min(...chartData.map(d => d.p5));
    const valueRange = maxValue - minValue;
    // Create simulation paths
    const numPaths = 50;
    for (let i = 0; i < numPaths; i++) {
      const points: THREE.Vector3[] = [];
      const angle = i / numPaths * Math.PI * 2;
      chartData.forEach((point, idx) => {
        const t = idx / (chartData.length - 1);
        const x = (t - 0.5) * 10;
        const interpolation = (Math.sin(angle) + 1) / 2;
        const value = point.p5 + (point.p95 - point.p5) * interpolation;
        const y = ((value - minValue) / valueRange - 0.5) * 8;
        const radius = 0.5 + interpolation * 0.5;
        const z = Math.cos(angle) * radius;
        points.push(new THREE.Vector3(x, y, z));
      });
      const curve = new THREE.CatmullRomCurve3(points);
      const curvePoints = curve.getPoints(50);
      const geometry = new THREE.BufferGeometry().setFromPoints(curvePoints);
      const material = new THREE.LineBasicMaterial({
        color: 0x4d7fc4,
        transparent: true,
        opacity: 0.15
      });
      const line = new THREE.Line(geometry, material);
      scene.add(line);
    }
    // Create median path (highlighted)
    const medianPoints: THREE.Vector3[] = chartData.map((point, idx) => {
      const t = idx / (chartData.length - 1);
      const x = (t - 0.5) * 10;
      const y = ((point.median - minValue) / valueRange - 0.5) * 8;
      return new THREE.Vector3(x, y, 0);
    });
    const medianGeometry = new THREE.BufferGeometry().setFromPoints(medianPoints);
    const medianMaterial = new THREE.LineBasicMaterial({
      color: 0x8b5cf6,
      linewidth: 2
    });
    const medianLine = new THREE.Line(medianGeometry, medianMaterial);
    scene.add(medianLine);
    // Mouse interaction
    let isDragging = false;
    let previousMousePosition = {
      x: 0,
      y: 0
    };
    let rotation = {
      x: 0,
      y: 0
    };
    const onMouseDown = (e: MouseEvent) => {
      isDragging = true;
      previousMousePosition = {
        x: e.clientX,
        y: e.clientY
      };
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const deltaX = e.clientX - previousMousePosition.x;
      const deltaY = e.clientY - previousMousePosition.y;
      rotation.y += deltaX * 0.005;
      rotation.x += deltaY * 0.005;
      // Limit vertical rotation
      rotation.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, rotation.x));
      previousMousePosition = {
        x: e.clientX,
        y: e.clientY
      };
    };
    const onMouseUp = () => {
      isDragging = false;
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY * 0.01;
      camera.position.multiplyScalar(1 + delta * 0.1);
      // Limit zoom
      const distance = camera.position.length();
      if (distance < 5) camera.position.setLength(5);
      if (distance > 20) camera.position.setLength(20);
    };
    container.addEventListener('mousedown', onMouseDown);
    container.addEventListener('mousemove', onMouseMove);
    container.addEventListener('mouseup', onMouseUp);
    container.addEventListener('wheel', onWheel, {
      passive: false
    });
    // Animation loop
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);
      // Apply rotation
      const radius = camera.position.length();
      camera.position.x = radius * Math.sin(rotation.y) * Math.cos(rotation.x);
      camera.position.y = radius * Math.sin(rotation.x);
      camera.position.z = radius * Math.cos(rotation.y) * Math.cos(rotation.x);
      camera.lookAt(0, 0, 0);
      // Subtle auto-rotation
      rotation.y += 0.001;
      renderer.render(scene, camera);
    };
    animate();
    // Handle resize
    const handleResize = () => {
      if (!containerRef.current || !camera || !renderer) return;
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', handleResize);
    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      container.removeEventListener('mousedown', onMouseDown);
      container.removeEventListener('mousemove', onMouseMove);
      container.removeEventListener('mouseup', onMouseUp);
      container.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', handleResize);
      if (renderer) {
        container.removeChild(renderer.domElement);
        renderer.dispose();
      }
      scene.traverse(object => {
        if (object instanceof THREE.Mesh || object instanceof THREE.Line) {
          object.geometry.dispose();
          if (Array.isArray(object.material)) {
            object.material.forEach(material => material.dispose());
          } else {
            object.material.dispose();
          }
        }
      });
    };
  }, [chartData, timeHorizon, currentValue]);
  if (!chartData || chartData.length === 0) {
    return <div className="w-full h-[500px] bg-slate-900/50 rounded-xl flex items-center justify-center" data-id="element-988">
        <p className="text-muted" data-id="element-989">No data to visualize</p>
      </div>;
  }
  return <div className="w-full h-[500px] bg-slate-900/50 rounded-xl overflow-hidden relative" data-id="element-990">
      <div ref={containerRef} className="w-full h-full" data-id="element-991" />
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-xs text-muted bg-slate-900/80 px-3 py-1 rounded-full" data-id="element-992">
        Drag to rotate • Scroll to zoom
      </div>
    </div>;
};
export default MonteCarloVisualization3D;